import { Fragment, useEffect, useRef, useState } from 'react';

// ── Parallax ──────────────────────────────────────────────────────────────
// Returns [ref, transform] — apply the transform to an element to make it drift
// as it scrolls through the viewport. speed ~0.15–0.4 is subtle & tasteful.
export function useParallax(speed = 0.25) {
  const ref = useRef(null);
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const rel = (rect.top + rect.height / 2 - vh / 2) / vh; // ~ -1..1
      setY(-rel * speed * 100);
    };
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); cancelAnimationFrame(raf); };
  }, [speed]);
  return [ref, `translate3d(0, ${y.toFixed(1)}px, 0)`];
}

// ── SpotlightCard ─────────────────────────────────────────────────────────
// reactbits "magic-bento" + "chroma-grid" style: a cursor-following radial glow
// inside the card plus a soft glowing border that lifts on hover.
export function SpotlightCard({ children, glow = '#0D9488', radius = 18, borderColor, style, onClick, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={(e) => { setHover(true); onMouseEnter?.(e); }}
      onMouseLeave={(e) => { setHover(false); onMouseLeave?.(e); }}
      onClick={onClick}
      style={{
        position: 'relative', borderRadius: radius, overflow: 'hidden',
        border: `1px solid ${hover ? glow : (borderColor || 'rgba(255,255,255,0.10)')}`,
        boxShadow: hover ? `0 18px 44px ${glow}33, 0 0 0 1px ${glow}55 inset` : '0 2px 10px rgba(0,0,0,0.06)',
        transform: hover ? 'translateY(-4px)' : 'none',
        transition: 'transform 0.28s cubic-bezier(.2,.7,.2,1), box-shadow 0.28s, border-color 0.28s',
        ...style,
      }}
    >
      <div style={{
        position: 'absolute', inset: 0, borderRadius: radius, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(230px circle at var(--mx, 50%) var(--my, 0%), ${glow}2e, transparent 62%)`,
        opacity: hover ? 1 : 0, transition: 'opacity 0.3s',
      }} />
      <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>{children}</div>
    </div>
  );
}

// ── PillNav ───────────────────────────────────────────────────────────────
// reactbits "pill-nav": rounded pill bar with a sliding highlight behind the
// active/hovered item. items: [{ label, href, onClick }].
export function PillNav({ items, accent = '#0D9488', textColor = '#1a1a2e', bg = 'rgba(255,255,255,0.6)' }) {
  const [active, setActive] = useState(null);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2, padding: 4, borderRadius: 999,
      background: bg, backdropFilter: 'blur(10px)', border: '1px solid rgba(0,0,0,0.06)',
    }}>
      {items.map((it, i) => (
        <a key={it.label} href={it.href}
          onClick={(e) => { it.onClick?.(e); }}
          onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
          style={{
            position: 'relative', padding: '7px 15px', borderRadius: 999, textDecoration: 'none',
            fontSize: '0.86rem', fontWeight: 600, whiteSpace: 'nowrap',
            color: active === i ? '#fff' : textColor,
            background: active === i ? accent : 'transparent',
            transition: 'color 0.2s, background 0.25s',
          }}>
          {it.label}
        </a>
      ))}
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────────
// reactbits "stepper": numbered circles with connecting lines that fill as you
// progress, checkmarks on completed steps, glowing active step. `current` is 1-based.
export function Stepper({ steps = [], current = 1, accent = '#0D9488' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 20px 6px' }}>
      {steps.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        const on = done || active;
        return (
          <Fragment key={label}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
                background: on ? accent : 'transparent', color: on ? '#fff' : '#94a3b8',
                border: `2px solid ${on ? accent : '#cbd5e1'}`,
                boxShadow: active ? `0 0 0 4px ${accent}22` : 'none',
                transition: 'background 0.3s, border-color 0.3s, box-shadow 0.3s, color 0.3s',
              }}>
                {done ? '✓' : n}
              </div>
              <span style={{ fontSize: '0.68rem', fontWeight: active ? 700 : 500, color: active ? accent : '#94a3b8', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '13px 8px 0', background: '#e2e8f0', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, width: done ? '100%' : '0%', background: accent, transition: 'width 0.45s ease' }} />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Chroma reveal (section-level) ───────────────────────────────────────────
// reactbits "chroma-grid" reveal, applied across a whole section: everything is
// de-saturated except a circle that follows the cursor. The veil also fades out
// at the top & bottom edges so it blends seamlessly into adjacent sections.
// Usage: give the <section> `position:relative` + `onMouseMove={chromaTrack}`,
// then drop <ChromaVeil/> in as a child.
export function chromaTrack(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--gx', `${e.clientX - r.left}px`);
  el.style.setProperty('--gy', `${e.clientY - r.top}px`);
}
export function ChromaVeil() {
  return <div className="fx-chroma-veil" aria-hidden />;
}

// ── Masonry ───────────────────────────────────────────────────────────────
// reactbits "masonry" + "magic-bento": staggered column image grid with hover
// zoom, a cursor-following sheen and an accent glow ring on each tile.
export function Masonry({ images = [], columns = 3, gap = 12, radius = 14, onImageClick, accent = '#0D9488' }) {
  const list = (images || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="fx-masonry" style={{ columnCount: columns, columnGap: gap }}>
      <style>{`
        .fx-mtile{position:relative;break-inside:avoid;overflow:hidden;}
        .fx-mtile img{width:100%;display:block;transition:transform .55s cubic-bezier(.2,.7,.2,1);}
        .fx-mtile:hover img{transform:scale(1.07);}
        .fx-mtile::after{content:'';position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:0;transition:opacity .3s;
          background:radial-gradient(180px circle at var(--mx,50%) var(--my,50%), rgba(255,255,255,.28), transparent 62%);
          box-shadow:inset 0 0 0 2px var(--fx-acc,#0D9488);}
        .fx-mtile:hover::after{opacity:1;}
      `}</style>
      {list.map((src, i) => (
        <div key={i} className="fx-mtile"
          style={{ '--fx-acc': accent, marginBottom: gap, borderRadius: radius, cursor: onImageClick ? 'zoom-in' : 'default' }}
          onClick={() => onImageClick?.(src)}
          onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`); e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`); }}>
          <img src={src} alt="" loading="lazy" style={{ borderRadius: radius }}
            onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
        </div>
      ))}
    </div>
  );
}
